import {
  extremosTampaTraseira,
  larguraTampaTraseira,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'
import { TONS_CARROCERIA } from '../../dominio/didatico/tokens'
import { orientacaoEntre } from '../componentes/Barra'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { MaterialPeca } from '../componentes/Peca'

export interface GeometriaQuinaTraseira {
  sinal: number
  xFora: number
  xTampa: number
  xDentro: number
  largura: number
  xCentro: number
  yCentro: number
  zCentro: number
  altura: number
  sobreposicao: number
}

/**
 * Calcula as medidas da quina traseira fixa da carroceria do hatch.
 * A borda de fora fica na face da lateral e a borda de dentro passa 5 mm por trás da borda da tampa.
 */
export function geometriaQuinaTraseira(perfil: PerfilVeiculo, sinal: number): GeometriaQuinaTraseira {
  const larguraTampa = larguraTampaTraseira(perfil)
  const sobreposicao = 0.005
  const xFora = sinal * (perfil.largura / 2)
  const xTampa = sinal * (larguraTampa / 2)
  const xDentro = sinal * (larguraTampa / 2 - sobreposicao)
  const largura = Math.abs(xFora - xDentro)
  const xCentro = (xFora + xDentro) / 2
  const { de, para } = extremosTampaTraseira(perfil)
  const altura = Math.hypot(para.y - de.y, para.z - de.z)
  const yCentro = (de.y + para.y) / 2
  const zCentro = (de.z + para.z) / 2

  return {
    sinal,
    xFora,
    xTampa,
    xDentro,
    largura,
    xCentro,
    yCentro,
    zCentro,
    altura,
    sobreposicao,
  }
}

interface PropsQuinas {
  perfil: PerfilVeiculo
  corAlvo?: string
}

/**
 * Quinas traseiras do hatch: peças fixas da carroceria que fecham a faixa
 * entre a borda da tampa e a face externa da lateral, da cintura ao para-choque.
 */
export function QuinasTraseirasHatch({ perfil, corAlvo }: PropsQuinas) {
  if (perfil.volumeTraseiro !== 'hatch') return null

  const { de, para } = extremosTampaTraseira(perfil)
  const quaternion = orientacaoEntre(de, para)

  return (
    <group name="quinas" data-testid="quinas-traseiras">
      {[-1, 1].map((sinal) => {
        const geo = geometriaQuinaTraseira(perfil, sinal)
        return (
          <group
            key={sinal}
            name="quinasTraseiras"
            position={[geo.xCentro, geo.yCentro, geo.zCentro]}
            quaternion={quaternion as [number, number, number, number]}
          >
            <mesh>
              <CaixaArredondada args={[geo.largura, geo.altura, 0.04]} raio={0.015} />
              {corAlvo ? (
                <meshPhysicalMaterial
                  color={corAlvo}
                  metalness={0.75}
                  roughness={0.28}
                  clearcoat={1}
                  clearcoatRoughness={0.06}
                  envMapIntensity={1}
                />
              ) : (
                <MaterialPeca
                  id={sinal < 0 ? 'paineisLateraisEsquerda' : 'paineisLateraisDireita'}
                  corBase={TONS_CARROCERIA.painelLateral}
                  acabamento="pintura"
                />
              )}
            </mesh>
          </group>
        )
      })}
    </group>
  )
}
